import { useCallback, useMemo, useState } from 'react';
import { useSessions } from '../../hooks/useSessions';

/**
 * The Citizen app's view of time: this session, or the one before it. Nothing older.
 * LEGISLATIVE_SESSION_MODEL_PRD.md §5.D3 (D-4).
 *
 * Citizens do not think in legislative sessions, so the app does not offer them
 * one. It offers two eras — what is live now, and what just finished — because
 * two is the smallest number that still answers "what happened to that bill I
 * was following?" and every era exposed is an era that can be accidentally
 * mixed.
 *
 * WHY THIS IS ENFORCED CLIENT-SIDE. The PRD sketched the API rejecting an
 * out-of-range session with a 400, which assumed Citizen had its own endpoints.
 * It does not — it calls the same `/bills` as Pro, so rejecting there would
 * either break Pro or require a per-request membership lookup to tell the two
 * callers apart. And the cap is not access control: legislative data is public
 * record, and a citizen who hand-edits a URL sees nothing they could not see on
 * the state's own site. It is a product rule about what the Citizen UI browses,
 * so it belongs in the Citizen UI, applied at the one place every Citizen query
 * reads its scope from.
 *
 * A bill DETAIL page stays reachable at any age, and a watched bill is never
 * dropped for being old — the cap governs browsing, not following. Losing sight
 * of something you deliberately chose to follow would read as data loss.
 */

export type CitizenEra = 'current' | 'previous';

export interface CitizenEraState {
  era: CitizenEra;
  setEra: (era: CitizenEra) => void;
  /** Session key for the selected era, or null before the registry loads. */
  sessionKey: string | null;
  /** Display label for the selected era's session, e.g. "2026 Regular Session". */
  label: string | null;
  /** The previous session's calendar year — for the "Concluded · 2025" badge. */
  previousYear: number | null;
  /**
   * Whether a previous session exists to switch to. False hides the control
   * outright (D-12) rather than disabling it: an era toggle with nothing on the
   * other side invites a question the data cannot answer yet.
   */
  canSwitch: boolean;
  /** Session keys the Citizen app may address at all — for scoping any query. */
  allowedSessionKeys: string[];
}

export function useCitizenEra(): CitizenEraState {
  const { sessions, currentSessionKey } = useSessions();
  const [era, setEra] = useState<CitizenEra>('current');

  return useMemo(() => {
    // Newest first, as the registry returns them; the entry after the current
    // one is "last session" regardless of whether it was regular or special —
    // a citizen asking what just finished means the one before this, not the
    // one before this of the same kind.
    const currentIndex = sessions.findIndex((s) => s.sessionKey === currentSessionKey);
    const current = currentIndex >= 0 ? sessions[currentIndex] : null;
    const previous = currentIndex >= 0 ? sessions[currentIndex + 1] ?? null : null;

    const selected = era === 'previous' && previous ? previous : current;

    return {
      era: previous ? era : 'current',
      setEra,
      sessionKey: selected?.sessionKey ?? null,
      label: selected?.label ?? selected?.shortLabel ?? null,
      previousYear: previous?.year ?? null,
      canSwitch: Boolean(previous),
      allowedSessionKeys: [current?.sessionKey, previous?.sessionKey].filter(
        (k): k is string => Boolean(k)
      ),
    };
  }, [sessions, currentSessionKey, era]);
}

/**
 * The year to show on a "Concluded · YYYY" badge, or null when the bill belongs
 * to the current session and needs no badge.
 *
 * Keyed on the BILL's own session, not the selected era, so it stays correct for
 * a watched bill older than the browsing cap — which still renders in the
 * watchlist and still needs marking.
 */
export function useConcludedYear() {
  const { currentSessionKey, sessions } = useSessions();
  return useCallback(
    (sessionKey: string | null | undefined): number | null => {
      if (!sessionKey || !currentSessionKey) return null;
      if (sessionKey === currentSessionKey) return null;
      return sessions.find((s) => s.sessionKey === sessionKey)?.year ?? null;
    },
    [currentSessionKey, sessions]
  );
}
